// Inloggningen: en länk med e-post, eller en passkey. Ett konto kommer genom en inbjudan från en
// ägare (b5) eller från e-VAtek; det finns ingen registrering. Provkontot med lösenord finns
// bara lokalt.

import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { useState } from "preact/hooks";
import { api, Felsvar, type Konfig } from "../api.ts";

function Provkontot(props: { inloggad: () => Promise<void> }) {
  const [epost, setEpost] = useState("");
  const [losenord, setLosenord] = useState("");
  const [fel, setFel] = useState<string | null>(null);
  const [vantar, setVantar] = useState(false);

  async function skicka(e: Event) {
    e.preventDefault();
    setFel(null);
    setVantar(true);
    try {
      await api.loggaIn(epost, losenord);
      await props.inloggad();
    } catch (err) {
      setFel(
        err instanceof Felsvar && err.status === 401
          ? "Fel e-post eller lösenord."
          : (err as Error).message,
      );
    } finally {
      setVantar(false);
    }
  }

  return (
    <details class="provkonto">
      <summary>Provkontot (bara lokalt)</summary>
      <form onSubmit={skicka}>
        <label>
          Provkontots e-post
          <input
            type="email"
            autoComplete="username"
            required
            value={epost}
            onInput={(e) => setEpost((e.target as HTMLInputElement).value)}
          />
        </label>
        <label>
          Lösenord
          <input
            type="password"
            autoComplete="current-password"
            required
            value={losenord}
            onInput={(e) => setLosenord((e.target as HTMLInputElement).value)}
          />
        </label>
        {fel && (
          <p class="fel" role="alert">
            {fel}
          </p>
        )}
        <button type="submit" class="knapp-huvud" disabled={vantar}>
          {vantar ? "Loggar in …" : "Logga in med lösenord"}
        </button>
      </form>
    </details>
  );
}

export function Inloggningsvy(props: { konfig: Konfig | null; inloggad: () => Promise<void> }) {
  const [epost, setEpost] = useState("");
  const [meddelande, setMeddelande] = useState<string | null>(null);
  const [fel, setFel] = useState<string | null>(null);
  const [vantar, setVantar] = useState(false);
  const passkey = browserSupportsWebAuthn();

  async function begarLank(e: Event) {
    e.preventDefault();
    setFel(null);
    setMeddelande(null);
    setVantar(true);
    try {
      setMeddelande((await api.begarLank(epost)).meddelande);
    } catch (err) {
      setFel((err as Error).message);
    } finally {
      setVantar(false);
    }
  }

  async function medPasskey() {
    setFel(null);
    try {
      await api.loggaInMedPasskey();
      await props.inloggad();
    } catch (err) {
      const namn = (err as Error).name;
      setFel(
        namn === "NotAllowedError" || namn === "AbortError"
          ? "Inloggningen med passkey avbröts."
          : "Inloggningen med passkey gick inte. Logga in med en länk i stället.",
      );
    }
  }

  return (
    <main class="inloggning">
      <div class="inloggning-ruta">
        <h1>Båtkoll</h1>
        <p class="liten">Logga in för att se hur båten mår.</p>
        <form onSubmit={begarLank}>
          <label>
            E-post
            <input
              type="email"
              autoComplete="username webauthn"
              required
              value={epost}
              onInput={(e) => setEpost((e.target as HTMLInputElement).value)}
            />
          </label>
          <button type="submit" class="knapp-huvud" disabled={vantar}>
            {vantar ? "Skickar …" : "Skicka inloggningslänk"}
          </button>
        </form>
        {meddelande && (
          <p class="ok-text" role="status">
            {meddelande}
          </p>
        )}
        {fel && (
          <p class="fel" role="alert">
            {fel}
          </p>
        )}
        {passkey && (
          <button type="button" class="knapp-andra" onClick={medPasskey}>
            Logga in med passkey
          </button>
        )}
        {props.konfig?.provinloggning && <Provkontot inloggad={props.inloggad} />}
      </div>
    </main>
  );
}

/** Sidan som länken i mejlet öppnar. Inloggningen sker först när ägaren trycker på knappen. */
export function Lankvy(props: { token: string; inloggad: () => Promise<void> }) {
  const [fel, setFel] = useState<string | null>(null);
  const [vantar, setVantar] = useState(false);

  async function loggaIn() {
    setFel(null);
    setVantar(true);
    try {
      await api.anvandLank(props.token);
      history.replaceState(null, "", "/#start");
      await props.inloggad();
    } catch (err) {
      setFel((err as Error).message);
    } finally {
      setVantar(false);
    }
  }

  return (
    <main class="inloggning">
      <div class="inloggning-ruta">
        <h1>Logga in på Båtkoll</h1>
        <p class="liten">Tryck på knappen för att logga in på den här enheten.</p>
        <button type="button" class="knapp-huvud bred" disabled={vantar} onClick={loggaIn}>
          {vantar ? "Loggar in …" : "Logga in"}
        </button>
        {fel && (
          <p class="fel" role="alert">
            {fel} <a href="/">Till inloggningen</a>
          </p>
        )}
      </div>
    </main>
  );
}
